import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WalletIcon from '@mui/icons-material/Wallet';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Input,
  Typography,
} from '@mui/material';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useRef, useState } from 'react';
import CustomButton from '../global/CustomButton';
import { useLocalState } from '../../utils/hooks/global/useLocalStorage';
import CheckIcon from '@mui/icons-material/Check';

const VeridianLogin = ({ onSubmit }) => {
  const [showInput, setShowInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('scanQR');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [jwt] = useLocalState(null, 'jwt');

  const isCameraRendered = useRef(false);
  const [copied, setCopied] = useState(false);
  const scannerRef = useRef(null);
  const [clientOobi, setClientOobi] = useState('');
  const [oobi, setOobi] = useState('');
  const [restartCamera, setRestartCamera] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const copyOobi = async () => {
    try {
      await navigator.clipboard.writeText(clientOobi);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = clientOobi;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  useEffect(() => {
    setShowInput(false);
  }, []);

  useEffect(() => {
    handleShowQr();
  }, [backendUrl]);

  useEffect(() => {
    if (!isCameraRendered.current && showInput) {
      console.log('Initializing QR Scanner');
      isCameraRendered.current = true;

      const scanner = new Html5QrcodeScanner(
        'qr-reader',
        {
          qrbox: {
            width: 250,
            height: 250,
          },
          fps: 5,
        },
        false
      );

      scannerRef.current = scanner;

      const success = (result) => {
        scanner.clear();
        if (result && result.includes('oobi')) {
          handleResolveOobi(result);
        } else {
          restartScanner();
        }
      };

      const error = (err) => {
        console.warn('QR scan error:', err);
      };

      scanner.render(success, error);
    }

    return () => {
      if (scannerRef.current && isCameraRendered.current) {
        try {
          scannerRef.current.clear();
        } catch (err) {
          console.warn('Error clearing scanner:', err);
        }
      }
    };
  }, [showInput]);

  const handleShowQr = async () => {
    try {
      fetch(`${backendUrl}/auth/signify/oobi`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + jwt,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          setLoading(false);
          setClientOobi(data.oobi);
          setShowQR(true);
        })
        .catch((error) => {
          console.error('There was an error retrieving client OOBI', error);
          setLoading(false);
        });
    } catch (error) {
      console.log('Error: ', error);
      setLoading(false);
    }
  };

  const handleResolveOobi = async (oobi) => {
    const isValidOobi = oobi && oobi.includes('oobi');

    if (!isValidOobi) {
      return restartScanner();
    }

    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/auth/signify/resolveOobi`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + jwt,
        },
        body: JSON.stringify({ oobi }),
      });

      if (response.ok) {
        const responseJSON = await response.json();
        if (responseJSON.data === 'OOBI resolved successfully') {
          setOobi(oobi);
          setStatus('showQR');
        }
      }
    } catch (error) {
      console.error('Error resolving OOBI:', error);
      setError('Failed to resolve OOBI');
      setStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const restartScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.clear();
      } catch (err) {
        console.warn('Error clearing scanner:', err);
      }
    }

    isCameraRendered.current = false;
    setShowInput(false);

    setTimeout(() => {
      setRestartCamera(!restartCamera);
    }, 100);
  };

  const handleIssueCredentials = async () => {
    try {
      const res = await fetch(`${backendUrl}/auth/signify/issueCredentials`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + jwt,
        },
      });
      const data = await res.json();

      console.log(data);

      if (!res.ok || data.success === false) {
        setStatus('failed');
        setError(data.error || 'Login request failed');
        return;
      }

      setStatus('issued');
    } catch (err) {
      console.error('Veridian login error:', err);
      setStatus('failed');
      setError('Network error starting login');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'scanQR':
        return (
          <Box textAlign="center">
            <Box>
              <Typography variant="h6" gutterBottom>
                Scan your Identifier's QR Code
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mt: '25px',
                }}
              >
                <CustomButton
                  color={'black'}
                  icon={CameraAltIcon}
                  buttonText={'Scan QR Code'}
                  onClick={() => setShowInput(true)}
                />
                <Input
                  placeholder="Or enter OOBI manually"
                  value={oobi}
                  onChange={(e) => setOobi(e.target.value)}
                  sx={{ width: '100%' }}
                />
                {oobi && (
                  <Button
                    variant="outlined"
                    onClick={() => handleResolveOobi(oobi)}
                    disabled={loading}
                    sx={{ width: '100%' }}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Submit OOBI'}
                  </Button>
                )}
              </Box>

              {showInput && (
                <Box sx={{ mt: 3 }}>
                  <div id="qr-reader" style={{ width: '100%' }}></div>
                  <CustomButton
                    color={'lRed'}
                    buttonText={'Cancel Scan'}
                    icon={CancelIcon}
                    onClick={restartScanner}
                    sx={{ mt: 2 }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        );
      case 'showQR':
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              Scan this QR code with your Identity Wallet and click "Issue
              Credentials"
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center',
                mt: '25px',
              }}
            >
              <QRCodeSVG value={clientOobi} size={280} />
              <CustomButton
                buttonText={copied ? 'Copied!' : 'Copy OOBI'}
                icon={ContentCopyIcon}
                color={'black'}
                onClick={copyOobi}
                sx={{ mt: 1 }}
              />
            </Box>
            <CustomButton
              buttonText={'Issue Credentials'}
              icon={WalletIcon}
              color={'black'}
              onClick={() => {
                handleIssueCredentials();
              }}
              sx={{ mt: 4 }}
            />
          </Box>
        );

      case 'issued':
        return (
          <Box textAlign="center">
            <Typography
              variant="h4"
              color="success.main"
              gutterBottom
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              gap={'10px'}
            >
              <CheckIcon fontSize="large" />
              Credentials are issued.{' '}
            </Typography>
            <Typography fontSize={'13px'}>
              {' '}
              Check the notifications in your identity wallet.
            </Typography>
            <CustomButton
              buttonText={'Done'}
              color={'black'}
              onClick={onSubmit}
              sx={{ mt: 4 }}
            />
          </Box>
        );

      case 'failed':
      case 'expired':
        return (
          <Box textAlign="center">
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <CustomButton
              color="black"
              buttonText={'Try Again'}
              icon={ReplayIcon}
              onClick={() => {
                setStatus('showQR');
                setError('');
                setShowInput(false);
              }}
              disabled={loading}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return renderContent();
};

export default VeridianLogin;
